import { SidebarContent, SidebarGroup, SidebarGroupContent, SidebarMenu } from '@talentmesh/ui/components/sidebar';
import { Skeleton } from '@talentmesh/ui/components/skeleton';
import { ChevronLeft } from 'lucide-react';
import { SyntheticEvent, useEffect } from 'react';
import { generatePath } from 'react-router';
import AISearchNavGroup from './AISearchNavGroup';
import ApplicantsListNavGroup from './ApplicantsListNavGroup';
import CandidatesListNavGroup from './CandidatesListNavGroup';
import LineMenuItem from './LineMenuItem';
import { CandidateTabs } from '@/Models/CandidateOverview';
import useAISearchCounters from '@/Pages/Candidates/AiSearch/api/useAISearchCounters';
import useAISearchProgress from '@/Pages/Candidates/AiSearch/api/useAISearchProgress';
import RoutePath from '@/Routing/RoutePath';
import UIStrings from '@/Utils/UIStrings';

export interface ProfileDetailsSidebarProps {
    clientId: string;
    companyId: string;
    recruitmentName?: string;
    jobLocation?: string;
    isAgenticSearchSelected: boolean;
    isIdentifiedAiSearchSelected: boolean;
    isShortlistedAiSearchSelected: boolean;
    selectedApplicantOverviewTab?: CandidateTabs;
    handleApplicantOverviewTabChange?: (event: SyntheticEvent, value: CandidateTabs) => void;
}

const CandidatesOverviewNavigationSidebar = ({
    clientId,
    companyId,
    recruitmentName,
    jobLocation,
    isAgenticSearchSelected,
    isIdentifiedAiSearchSelected,
    isShortlistedAiSearchSelected,
    selectedApplicantOverviewTab,
    handleApplicantOverviewTabChange,
}: ProfileDetailsSidebarProps) => {
    const { mutate: mutateAiSearchCounter } = useAISearchCounters();
    const { data: aiSearchProgress } = useAISearchProgress();

    useEffect(() => {
        if (aiSearchProgress?.isInProgress === false) {
            mutateAiSearchCounter();
        }
    }, [aiSearchProgress, mutateAiSearchCounter]);

    return (
        <SidebarContent className="gap-6 py-1">
            <SidebarGroup className="p-0">
                <SidebarGroupContent>
                    <SidebarMenu>
                        <LineMenuItem
                            isActive="never"
                            to={generatePath(RoutePath.AssessmentsActive, { clientId, companyId })}
                        >
                            <ChevronLeft className="h-4 w-4 shrink-0" />
                            <span data-cy="backButton" className="text-sm font-semibold">
                                {UIStrings.Back}
                            </span>
                        </LineMenuItem>
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>

            <SidebarGroup className="p-0">
                <SidebarGroupContent>
                    <SidebarMenu>
                        <div className="border-border text-foreground mx-2 flex flex-col gap-1 rounded-md border p-2">
                            {recruitmentName ? (
                                <span className="text-md font-semibold">{recruitmentName}</span>
                            ) : (
                                <Skeleton className="h-6 w-[18ch]" />
                            )}
                            {jobLocation ? (
                                <span className="text-muted-foreground text-sm font-light">{jobLocation}</span>
                            ) : (
                                <Skeleton className="h-4 w-[12ch]" />
                            )}
                        </div>
                    </SidebarMenu>
                </SidebarGroupContent>
            </SidebarGroup>

            <AISearchNavGroup isActive={isAgenticSearchSelected} />

            <CandidatesListNavGroup
                isIdentifiedAiSearchSelected={isIdentifiedAiSearchSelected}
                isShortlistedAiSearchSelected={isShortlistedAiSearchSelected}
            />

            <ApplicantsListNavGroup
                isApplicantOverviewPage={
                    !isIdentifiedAiSearchSelected && !isShortlistedAiSearchSelected && !isAgenticSearchSelected
                }
                handleApplicantOverviewTabChange={handleApplicantOverviewTabChange}
                selectedApplicantOverviewTab={selectedApplicantOverviewTab}
            />
        </SidebarContent>
    );
};

export default CandidatesOverviewNavigationSidebar;
